import React from 'react';
import { BACKEND_URL } from '../services/api';

const OfficialAdmissionPrint = ({ student, classes = [], printedBy = "Admin" }) => {
  if (!student) return null;

  const formatDate = (dateString) => {
    if (!dateString) return '....................';
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  // Helper to render checkbox
  const renderCheckbox = (label, checked) => (
    <div className="flex items-center gap-2 mr-6">
      <span className="text-2xl leading-none">{checked ? '☑' : '☐'}</span>
      <span className="text-lg">{label}</span>
    </div>
  );

  // Helper for dotted underline inputs
  const Field = ({ label, value, className = "", flex = true }) => (
    <div className={`flex ${flex ? 'items-end' : 'flex-col'} gap-2 ${className}`}>
      {label && <span className="text-lg font-bold whitespace-nowrap">{label}:</span>}
      <div className="flex-1 border-b border-dotted border-black min-h-[1.6rem] text-lg px-2 font-medium leading-tight">
        {value || ''}
      </div>
    </div>
  );

  return (
    <div className="form-container bg-white text-black leading-tight mx-auto">
      <style>{`
        @media screen {
          .form-container {
            box-shadow: 0 0 10px rgba(0,0,0,0.1);
            margin-top: 2rem;
            margin-bottom: 2rem;
            width: 210mm;
            min-height: 297mm;
            padding: 10mm;
          }
        }
        @media print {
          body {
            margin: 0;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
            background: white !important;
            font-family: "Times New Roman", Times, serif !important;
          }
          .no-print {
            display: none;
          }
          .form-container {
            width: 210mm;
            height: 297mm;
            padding: 15mm;
            margin: 0;
            box-shadow: none;
            display: block !important;
          }
          @page {
            size: A4 portrait;
            margin: 0;
          }
        }
        .form-container {
          font-family: "Times New Roman", Times, serif !important;
        }
        .underline-text {
          border-bottom: 1px solid black;
          display: inline-block;
          padding-bottom: 1px;
        }
      `}</style>

      {/* Header Section */}
      <div className="flex justify-between items-start mb-6">
        <div className="w-24">
          <img src={process.env.PUBLIC_URL + '/logo.png'} alt="CSK Logo" className="w-full object-contain" />
        </div>
        
        <div className="text-center flex-1 px-4">
          <h1 className="text-2xl font-bold tracking-tight">C.S.K Language & Consultancy P. Ltd.</h1>
          <p className="text-sm">Kalanki-14, Kathmandu, Nepal</p>
          <div className="mt-2">
            <h2 className="text-lg font-bold border-b border-black inline-block px-4">ADMISSION FORM</h2>
          </div>
        </div>

        <div className="w-32 flex flex-col items-end text-right">
          <p className="text-[10px] font-bold mb-1">Regd. No: 48708/064/065</p>
          <p className="text-[9px] font-bold text-black uppercase tracking-tighter">Printed By: {printedBy || "Authorized Staff"}</p>
          <div className="w-28 h-32 border border-black flex items-center justify-center overflow-hidden bg-gray-50 mt-2">
            {student.photo_url ? (
                <img 
                    src={student.photo_url.startsWith('http') ? student.photo_url : `${BACKEND_URL}${student.photo_url}`} 
                    alt="Student" 
                    className="w-full h-full object-cover"
                />
            ) : (
                <span className="text-[10px] text-gray-400 text-center px-2">PP Size Photo</span>
            )}
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {/* Row 1 */}
        <div className="grid grid-cols-2 gap-4">
          <Field label="Full Name" value={student.full_name} className="col-span-2" />
        </div>

        {/* Row 2 */}
        <div className="grid grid-cols-2 gap-4">
          <Field label="Address (Permanent)" value={student.permanent_address} />
          <Field label="Address (Temporary)" value={student.temporary_address} />
        </div>

        {/* Row 3 */}
        <div className="grid grid-cols-12 gap-8">
          <Field label="Contact No" value={student.phone} className="col-span-4" />
          <Field label="Email" value={student.email} className="col-span-5" />
          <Field label="Nationality" value={student.nationality} className="col-span-3" />
        </div>

        {/* Row 4 */}
        <div className="grid grid-cols-12 gap-8">
          <Field label="Date of Birth" value={formatDate(student.date_of_birth)} className="col-span-4" />
          <div className="flex items-end gap-3 col-span-4">
            <span className="text-lg font-bold">Sex:</span>
            {renderCheckbox("Male", student.sex === 'Male')}
            {renderCheckbox("Female", student.sex === 'Female')}
          </div>
          <Field label="Passport No" value={student.passport_no} className="col-span-4" />
        </div>

        {/* Row 5: Parent Info */}
        <div className="grid grid-cols-2 gap-4">
          <Field label="Father's Name" value={student.father_name} />
          <Field label="Contact No" value={student.father_contact} />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Mother's Name" value={student.mother_name} />
          <Field label="Contact No" value={student.mother_contact} />
        </div>

        {/* Row 6: Guardian Info */}
        <div className="grid grid-cols-12 gap-8">
          <Field label="Local Guardian" value={student.guardian_name} className="col-span-5" />
          <Field label="Relation" value={student.guardian_relation} className="col-span-3" />
          <Field label="Contact No" value={student.guardian_contact} className="col-span-4" />
        </div>

        {/* Row 7: Education */}
        <div className="grid grid-cols-3 gap-4">
          <Field label="Last Qualification" value={student.qualification || '....................'} />
          <Field label="Passed Year" value={student.passed_out_year} />
          <Field label="GPA/Percentage" value={student.gpa} />
        </div>

        {/* Row 8: Preparation Classes */}
        <div className="border border-black p-4 mt-4">
          <p className="text-xl font-bold mb-3 uppercase">Subject / Language Interested in:</p>
          <div className="flex flex-wrap gap-y-3">
            {classes.map(c => {
                const isEnrolled = student.classes?.some(sc => sc.id === c.id);
                return (
                    <div key={c.id} className="w-1/3">
                        {renderCheckbox(c.name, isEnrolled)}
                    </div>
                );
            })}
          </div>
          <Field label="Others" value="" className="mt-3" />
        </div>

        {/* Row 9: Visa & Country */}
        <div className="grid grid-cols-2 gap-8 mt-6">
          <div className="flex items-end gap-4">
            <span className="text-lg font-bold">Visa Type:</span>
            {renderCheckbox("Student", student.visa_type === 'Student')}
            {renderCheckbox("Working", student.visa_type === 'Working')}
          </div>
          <Field label="Target Country" value={student.abroad_study_country} />
        </div>

        {/* Terms & Conditions */}
        <div className="mt-8 border border-black p-5 text-[14px] leading-snug">
          <p className="font-bold text-xl mb-3 uppercase underline">Declaration & Terms</p>
          <ul className="list-decimal pl-6 space-y-1">
            <li>I hereby declare that the information provided above is true to the best of my knowledge.</li>
            <li>Classes will run from Sunday to Friday. Attendance is strictly required.</li>
            <li>Fees are non-refundable once the course has commenced or if the student stops attending.</li>
            <li>The consultancy is not responsible for any personal documents lost if not handed over officially.</li>
            <li>I agree to abide by all the rules and regulations of C.S.K Language & Consultancy P. Ltd.</li>
          </ul>
        </div>

        {/* Signatures */}
        <div className="mt-16 flex justify-between h-28 items-end">
          <div className="w-64 text-center">
            <div className="border-t border-black mb-2"></div>
            <p className="text-lg font-bold uppercase">Student's Signature</p>
            <p className="text-sm">Date: {new Date().toLocaleDateString()}</p>
          </div>
          <div className="w-64 text-center">
            <div className="border-t border-black mb-2"></div>
            <p className="text-lg font-bold uppercase">Authorized Signature</p>
            <p className="text-sm">(Office Seal)</p>
          </div>
        </div>
      </div>

      <div className="mt-10 text-center text-[11px] text-gray-400 border-t border-gray-100 pt-4">
        This is a computer-generated admission form for official records.
      </div>
    </div>
  );
};

export default OfficialAdmissionPrint;
